import { STALE_TIME } from 'business/constants';
import {
    operations,
    DataTypes,
    GenericSelectionBase,
} from 'business/definitions';

import { useQuery } from '@tanstack/react-query';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import i18n from 'i18n';
import { filterTreeValues } from './helpers';

/* Return the operations as options for a specific type. */
export const getOperationOptions = (
    type: DataTypesT,
): GenericSelectionBase<FilterOperationsT | DynamicFilterOperationsT>[] =>
    type in operations
        ? operations[type].map((o) => ({
              label: i18n.t(`sentence:${o}`),
              value: o,
              isBadValue: false,
          }))
        : [];

export const useGetFilterValues = (
    filter:
        | SentenceFilterWithScopesT
        | (DashboardFilterT & WithScopes)
        | undefined,
    disabled?: boolean | null,
    preProcessingUrlCallback: (url: string) => string = (url) => url,
    shouldFilterValuesByScope: boolean = false,
) => {
    const enabled = Boolean(
        filter &&
            !disabled &&
            filter.field !== '' &&
            [DataTypes.tree, DataTypes.category].includes(filter.type),
    );
    /*
        return type here is actually:
            type FilterWithValues = AvailableElementT & {
                values: { label: string; name: string; value: string }[];
                treeValues: FilterTreeValues;
            };

            with these sub types:
                type FilterTreeValues = {
                    name: string;
                    value: string;
                    children: FilterTreeItem[];
                };

                type FilterTreeItem = FilterTreeValues & { id: string; label: string };

        but other parts of the codebase are not typed correctly; hard until we've typed all our endpoints responses correctly.
        TODO: fix this.
    */
    return useQuery<SentenceFilterT, HttpError>({
        queryKey: ['useGetFiltersValues', filter?.field],

        queryFn: async () => {
            const response = await RQAPICallWithData<SentenceFilterT>({
                endpoint: `graph/get_filter_values?filter=${filter?.field}`,
                method: 'GET',
                preProcessingUrlCallback,
            });

            const scopeValues =
                filter?.scopes?.flatMap((scope) => scope.values) ?? [];

            if (!shouldFilterValuesByScope || scopeValues.length <= 0)
                return response.content.data;

            const values = response.content.data.values?.filter((filterValue) =>
                scopeValues.includes(filterValue.value),
            );
            const treeValues = response.content.data.treeValues
                ? {
                      ...response.content.data.treeValues,
                      children: filterTreeValues(
                          response.content.data.treeValues.children,
                          scopeValues,
                      ),
                  }
                : undefined;
            return {
                ...response.content.data,
                values,
                treeValues,
            };
        },

        enabled,

        retry: (failureCount, error) =>
            error.status !== 403 && failureCount < 1,

        staleTime: STALE_TIME,
    });
};
